import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ClientDashboardJourneyBar } from "./client/ClientDashboardJourneyBar";
import { ClientDashboardProgressHeader } from "./client/ClientDashboardProgressHeader";
import { ClientDashboardReadySection } from "./client/ClientDashboardReadySection";
import { ClientDashboardWelcomeSection } from "./client/ClientDashboardWelcomeSection";
import { ClientDashboardAnalyticsSection } from "./client/ClientDashboardAnalyticsSection";
import { useDashboardAssessmentData } from "../hooks/useDashboardAssessmentData";
import { useAuth } from "../auth/AuthContext";
import { useMenu } from "../Common/MenuContext";

interface ActionItem {
  title: string;
  description: string;
  to: string;
  label: string;
  dataElement?: string;
}

interface DashboardProjectContentProps {
  state: number;
  userName?: string;
  projectName?: string;
  companyName?: string;
  capabilities: Array<{
    id: number;
    capabilityName: string;
    capabilityDescription?: string;
    subCapabilities: Array<{ subId: number; subCapabilityName: string }>;
  }>;
  selectedCapabilities: Array<{ capabilityId: number; subCapabilityId: number }>;
  selectedCounts: Record<number, number>;
}

const REPORT_ACTION: ActionItem = {
  title: "Report available",
  description: "Your SME has submitted the final assessment report.",
  to: "/reports",
  label: "View report",
};

const CHAT_ACTION_UNREAD: ActionItem = {
  title: "New message from SME",
  description: "Check the latest guidance on your capability assessment.",
  to: "/chat",
  label: "View chat",
};

function getActionsForState(state: number): ActionItem[] {
  switch (state) {
    case 0:
      return [
        {
          title: "Ready to get started?",
          description: "Select the capabilities you want to assess.",
          to: "/capabilities",
          label: "Capabilities",
          dataElement: "capabilities",
        },
      ];
    case 1:
      return [
        {
          title: "Choose focus areas",
          description: "Review and update your selected capabilities.",
          to: "/capabilities",
          label: "Capabilities",
        },
        {
          title: "Complete your assessment",
          description: "Answer the assessment questions for your selected areas.",
          to: "/assessment",
          label: "Assessment",
        },
      ];
    case 2:
      return [
        {
          title: "Review capabilities",
          description: "Confirm your selected capabilities before submitting.",
          to: "/capabilities",
          label: "Capabilities",
        },
        {
          title: "Submit assessment",
          description: "Your assessment questions still need to be submitted.",
          to: "/assessment",
          label: "View assessment",
        },
      ];
    case 3:
      return [
        {
          title: "Assessment submitted",
          description: "Your assessment is with the SME team. You will be notified when there is an update.",
          to: "/assessment",
          label: "View assessment",
        },
      ];
    case 4:
      return [CHAT_ACTION_UNREAD];
    case 5:
      return [
        {
          title: "Awaiting SME response",
          description: "Continue the conversation while your assessment is reviewed.",
          to: "/chat",
          label: "View chat",
        },
      ];
    case 6:
      return [REPORT_ACTION];
    default:
      return [];
  }
}

function shouldShowAssessmentCharts(state: number, ratedCount: number) {
  return state >= 2 || ratedCount > 0;
}

function buildDashboardActions(
  state: number,
  hasUnreadReport: boolean,
  hasUnreadChat: boolean
): ActionItem[] {
  const actions = [...getActionsForState(state)];

  // Keep both notification cards visible when chat/report unread overlap with other states.
  if (
    hasUnreadReport &&
    !actions.some((action) => action.to === "/reports")
  ) {
    actions.unshift(REPORT_ACTION);
  }

  if (
    hasUnreadChat &&
    !actions.some((action) => action.to === "/chat")
  ) {
    actions.unshift(CHAT_ACTION_UNREAD);
  }

  return actions;
}

function ActionCardsList({
  actions,
  hasUnreadChat,
  hasUnreadReport,
}: {
  actions: ActionItem[];
  hasUnreadChat: boolean;
  hasUnreadReport: boolean;
}) {
  if (!actions.length) return null;

  return (
    <div className="client-dash__actions dash-action-list">
      {actions.map((action) => {
        const isChatAction = action.to === "/chat";
        const isReportAction = action.to === "/reports";
        const isNew =
          (isChatAction && hasUnreadChat) || (isReportAction && hasUnreadReport);

        let title = action.title;
        let description = action.description;
        if (isChatAction && !hasUnreadChat) {
          title = "Conversation with your SME";
          description = "You're all caught up — continue the conversation anytime.";
        }
        if (isReportAction && !hasUnreadReport) {
          title = "Your report";
          description = "Revisit your evaluation and recommendations anytime.";
        }

        return (
          <div
            className={`dash-action-card${isNew ? " dash-action-card--new" : ""}`}
            key={`${action.to}-${action.title}`}
          >
            <div className="dash-action-card__body">
              <p className="dash-action-card__title">
                {title}
                {isNew ? <span className="dash-action-card__badge">New</span> : null}
              </p>
              <p className="dash-action-card__text">{description}</p>
            </div>
            <Link
              to={action.to}
              className="app-btn app-btn--primary app-btn--sm dash-action-btn"
              data-element={action.dataElement}
            >
              {action.label}
            </Link>
          </div>
        );
      })}
    </div>
  );
}

const DashboardProjectContent: React.FC<DashboardProjectContentProps> = ({
  state,
  userName,
  projectName,
  companyName,
  selectedCounts,
}) => {
  const { user } = useAuth();
  const { hasUnreadChat, hasUnreadReport } = useMenu();
  const projectId = localStorage.getItem("projectId");
  const { data: assessmentData, loading: assessmentLoading, error: assessmentError } =
    useDashboardAssessmentData(user?.userId, projectId);

  const actions = buildDashboardActions(
    state,
    Boolean(hasUnreadReport),
    Boolean(hasUnreadChat)
  );
  const subCapabilityCount = useMemo(
    () =>
      Object.values(selectedCounts).reduce(
        (sum, count) => sum + (Number(count) || 0),
        0
      ),
    [selectedCounts]
  );

  const ratedCount = useMemo(() => {
    if (!assessmentData?.rows?.length) return 0;
    return assessmentData.rows.filter(
      (row) =>
        row.currentStatusRating != null && row.futureStatusRating != null
    ).length;
  }, [assessmentData?.rows]);

  const showCharts = shouldShowAssessmentCharts(state, ratedCount);
  const hasRatingContent =
    assessmentLoading || ratedCount > 0 || Boolean(assessmentData?.hasRatingData);
  const showInsights = showCharts && hasRatingContent;
  const showRadar = showCharts && hasRatingContent;
  const showWordCloud =
    showCharts && (assessmentLoading || Boolean(assessmentData?.hasDescriptionData));
  const showSlider = showCharts && hasRatingContent;

  const hasSelection = subCapabilityCount > 0;
  const hasInsights = showCharts && hasRatingContent;
  const phaseWelcome = !hasSelection;
  const phaseReady = hasSelection && !hasInsights && state <= 2;
  const phaseInsights = hasSelection && !phaseReady;

  const workflowActions = actions.filter(
    (action) => action.to !== "/chat" && action.to !== "/reports"
  );
  const notificationActions = actions.filter(
    (action) => action.to === "/chat" || action.to === "/reports"
  );

  const showAnalyticsZone = phaseReady || phaseInsights;
  const analyticsMode =
    phaseInsights && hasRatingContent ? "live" : "preview";

  return (
    <div className="client-dash dash-home">
      <ClientDashboardProgressHeader
        userName={userName}
        projectName={projectName}
        companyName={companyName}
        setupPhase={phaseWelcome}
        insightsPhase={phaseInsights}
      />
      <ClientDashboardJourneyBar
        state={state}
        capabilityCount={subCapabilityCount}
        variant="horizontal"
      />

      {phaseWelcome ? (
        <section className="client-dash__section" aria-label="Get started">
          <ClientDashboardWelcomeSection />
        </section>
      ) : null}

      {phaseReady ? (
        <section className="client-dash__section" aria-label="Your next steps">
          <ClientDashboardReadySection
            capabilityCount={subCapabilityCount}
            ratedCount={ratedCount}
            totalToRate={subCapabilityCount}
          />
        </section>
      ) : null}

      {showAnalyticsZone ? (
        <section
          className="client-dash__section client-dash__section--analytics"
          aria-label="Maturity insights"
        >
          <ClientDashboardAnalyticsSection
            mode={analyticsMode}
            assessmentData={assessmentData}
            loading={assessmentLoading}
            error={assessmentError}
            showInsights={showInsights}
            showRadar={showRadar}
            showSlider={showSlider}
            showWordCloud={showWordCloud}
          />
        </section>
      ) : null}

      {notificationActions.length > 0 ? (
        <section
          className="client-dash__section client-dash__section--notifications"
          aria-labelledby="client-dash-notifications"
        >
          <h2
            id="client-dash-notifications"
            className="client-dash__section-title app-heading-accent"
          >
            Updates
          </h2>
          <ActionCardsList
            actions={notificationActions}
            hasUnreadChat={Boolean(hasUnreadChat)}
            hasUnreadReport={Boolean(hasUnreadReport)}
          />
        </section>
      ) : null}

      {phaseInsights && workflowActions.length > 0 && state >= 3 ? (
        <section
          className="client-dash__section"
          aria-labelledby="client-dash-next-steps-insights"
        >
          <h2
            id="client-dash-next-steps-insights"
            className="client-dash__section-title app-heading-accent"
          >
            What&apos;s next
          </h2>
          <ActionCardsList
            actions={workflowActions}
            hasUnreadChat={Boolean(hasUnreadChat)}
            hasUnreadReport={Boolean(hasUnreadReport)}
          />
        </section>
      ) : null}

    </div>
  );
};

export default DashboardProjectContent;
